import { useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import {
  useCreateVendorProposalRequestMutation,
  useCreatePurchaseVendorProposalRequestMutation,
  useRevokeVendorProposalMutation,
  useReactivateVendorProposalMutation,
} from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { warnMissingInviteEmails } from "../../actionResultFeedback"
import { proposalLineRef } from "../../constants"

const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

const defaultDeadline = () => toLocalInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))

// Re-posting the create API with the same RFQ fields overwrites them, so the
// body is rebuilt from the proposal itself. Items go back WITHOUT proposedAmount
// — sending a price for every item would wipe the pin/url.
const buildRfqBody = (proposal, expiredAt) => ({
  supplierIds: [proposal.supplierId ?? proposal.supplier?.id],
  title: proposal.title,
  terms: Array.isArray(proposal.terms)
    ? proposal.terms
    : (proposal.termsText || "").split("\n").filter(Boolean),
  requiredDocuments: proposal.requiredDocuments,
  deliveryLocation: proposal.deliveryLocation,
  deliveryDate: proposal.deliveryDate,
  noteForVendor: proposal.noteForVendor,
  contactName: proposal.contactName,
  contactEmail: proposal.contactEmail,
  contactPhone: proposal.contactPhone,
  pinEnabled: proposal.pinEnabled,
  items: (proposal.items || []).map(proposalLineRef),
  deliveryFee: proposal.deliveryFee ?? undefined,
  expiredAt,
})

export default function ProposalDeadlineActions({ proposal, sourceType, sourceId, disabled }) {
  const [dialog, setDialog] = useState(null)
  const [deadline, setDeadline] = useState("")
  const [createOrderProposal, { isLoading: extendingOrder }] = useCreateVendorProposalRequestMutation()
  const [createPurchaseProposal, { isLoading: extendingPurchase }] = useCreatePurchaseVendorProposalRequestMutation()
  const [revokeProposal, { isLoading: revoking }] = useRevokeVendorProposalMutation()
  const [reactivateProposal, { isLoading: reactivating }] = useReactivateVendorProposalMutation()
  const busy = extendingOrder || extendingPurchase || revoking || reactivating
  const isPurchase = sourceType === "purchase"
  const tagArgs = {
    orderRequestId: isPurchase ? undefined : proposal.orderRequestId ?? sourceId,
    purchaseId: isPurchase ? sourceId : undefined,
  }

  const openExtend = () => {
    setDeadline(defaultDeadline())
    setDialog("extend")
  }
  const openReactivate = () => {
    setDeadline(defaultDeadline())
    setDialog("reactivate")
  }

  const handleReactivate = async () => {
    try {
      await reactivateProposal({
        id: proposal.id,
        expiredAt: new Date(deadline).toISOString(),
        ...tagArgs,
      }).unwrap()
      showToastSuccess("Đã mở lại link. Hãy gửi link mới cho nhà cung cấp.")
      setDialog(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể mở lại link. Vui lòng thử lại."))
    }
  }
  const close = () => !busy && setDialog(null)

  const handleExtend = async () => {
    const body = buildRfqBody(proposal, new Date(deadline).toISOString())
    try {
      let created
      if (isPurchase) {
        created = await createPurchaseProposal({ id: sourceId, ...body }).unwrap()
      } else {
        created = await createOrderProposal({
          orderRequestId: proposal.orderRequestId ?? (proposal.groupOrderId == null ? sourceId : undefined),
          groupOrderId: proposal.groupOrderId ?? undefined,
          ...body,
        }).unwrap()
      }
      showToastSuccess("Đã gia hạn deadline.")
      warnMissingInviteEmails(created)
      setDialog(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể gia hạn deadline. Vui lòng thử lại."))
    }
  }

  const handleRevoke = async () => {
    try {
      await revokeProposal({ id: proposal.id, ...tagArgs }).unwrap()
      showToastSuccess("Đã thu hồi link.")
      setDialog(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể thu hồi link. Vui lòng thử lại."))
    }
  }

  return (
    <MDBox display="flex" gap={0.5} flexWrap="wrap">
      <MDButton size="small" variant="text" color="info" disabled={disabled} onClick={openExtend}>
        Extend
      </MDButton>
      <MDButton size="small" variant="text" color="error" disabled={disabled} onClick={() => setDialog("revoke")}>
        Revoke
      </MDButton>
      <MDButton size="small" variant="text" color="success" disabled={disabled} onClick={openReactivate}>
        Re-activate
      </MDButton>

      <Dialog open={dialog === "reactivate"} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>Mở lại link báo giá</DialogTitle>
        <DialogContent>
          <MDBox pt={1} display="flex" flexDirection="column" gap={1.5}>
            <MDTypography variant="caption" color="text">
              {proposal.supplierName || proposal.supplier?.name} sẽ nhận link + PIN mới để sửa báo giá. Link cũ
              ngừng hoạt động.
            </MDTypography>
            <input
              type="datetime-local"
              value={deadline}
              min={toLocalInput(new Date())}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={busy}
              style={{ width: "100%", fontSize: 14, padding: "9px 10px", border: "1px solid #d2d6da", borderRadius: 6 }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={close} disabled={busy}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleReactivate}
            disabled={busy || !deadline || new Date(deadline) <= new Date()}
          >
            {busy ? "Saving…" : "Re-activate"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "extend"} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>Gia hạn deadline</DialogTitle>
        <DialogContent>
          <MDBox pt={1}>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              {proposal.supplierName || proposal.supplier?.name} — link và PIN giữ nguyên.
            </MDTypography>
            <input
              type="datetime-local"
              value={deadline}
              min={toLocalInput(new Date())}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={busy}
              style={{ width: "100%", fontSize: 14, padding: "9px 10px", border: "1px solid #d2d6da", borderRadius: 6 }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={close} disabled={busy}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleExtend}
            disabled={busy || !deadline || new Date(deadline) <= new Date()}
          >
            {busy ? "Saving…" : "Extend"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "revoke"} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>Thu hồi link</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Link hiện tại của {proposal.supplierName || proposal.supplier?.name} sẽ hết hạn ngay.
            {proposal.pinEnabled === false
              ? " Proposal này không dùng PIN nên gia hạn lại sẽ làm link cũ sống lại."
              : " PIN sẽ được đổi; muốn mở lại phải gia hạn rồi gửi link mới."}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={close} disabled={busy}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleRevoke} disabled={busy}>
            {busy ? "Revoking…" : "Revoke"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  )
}
